import './MultiTables.styles.css';

import _ from 'lodash';
import { observer } from 'mobx-react';
import React, { FC, useCallback, useMemo, useState } from 'react';

import { GameContext } from '../../../context/GameContext/GameContext';
import { useBackgroundTracker } from '../../../hooks/useBackgroundTracker';
import { useDateTimeSync } from '../../../hooks/useDateTimeSync';
import useScreenDimensions from '../../../hooks/useScreenDimensions';
import { useViewingTablesPopulator } from '../../../hooks/useViewingTablesPopulator';
import { useWebsocketSubscription } from '../../../hooks/useWebsocketSubscription';
import { Permission } from '../../../model/enumeration/Permission.enum';
import authStore from '../../../store/AuthStore';
import commonStore from '../../../store/CommonStore';
import eventLogsStore from '../../../store/EventLogsStore';
import permissionStore from '../../../store/PermissionStore';
import tableStore from '../../../store/TableStore';
import { WEventsDispatcher } from '../../../websocket/handlers/WEventsDispatcher';
import SwitchBlindsChipsButton from '../../shared/Button/SwitchBlindsChipsButton/SwitchBlindsChipsButton';
import { allButtonTypes } from '../../shared/Button/TableButton.types';
import Game from '../Game';
import { GameControlButtons } from '../Game.props';
import GameLogs from '../GameLogs/GameLogs';
import TableHeader from '../TableHeader/TableHeader';

const MIN_SCALE_SCREEN_WIDTH = 1600;

const isNeedToScale = (screenWidth: number): boolean =>
  screenWidth > MIN_SCALE_SCREEN_WIDTH;

const MultiTables: FC = () => {
  const [singleTableGameControlButtons, setSingleTableGameControlButtons] =
    useState<GameControlButtons>({
      isVertical: false,
      isVisible: false,
      tableId: '',
    });

  const tablesStats = useMemo(
    () => tableStore.getTablesStats(),
    [
      tableStore.activeTables.size,
      tableStore.filterStateChanged,
      tableStore.selectedFilterOptions,
    ],
  );

  const { screenWidth } = useScreenDimensions();

  const isSingleTable = useMemo(
    () => tableStore.viewingTableIds.length === 1,
    [tableStore.viewingTableIds.length],
  );

  const contextValue = useMemo(() => ({ isSingleTable }), [isSingleTable]);

  const hasManageBotsSeatingPermission = useMemo(
    () => permissionStore.hasPermission(Permission.MANAGE_BOTS_SEATING),
    [authStore.authenticatedUser],
  );

  useViewingTablesPopulator();

  useBackgroundTracker();

  useWebsocketSubscription(
    '/user/tables/sync',
    WEventsDispatcher.processEventMessage,
  );

  useWebsocketSubscription(
    '/user/tables/waitlist-sync',
    WEventsDispatcher.processEventMessage,
    undefined,
    hasManageBotsSeatingPermission,
  );

  useDateTimeSync();

  const findViewingTable = useCallback(
    (tableId: string) => tableStore.getActiveTableById(tableId),
    [tableStore.activeTables, tableStore.activeTables.size],
  );

  const sortedViewingTableIds = [
    ..._.intersection(tableStore.pinnedTableIds, tableStore.viewingTableIds),
    ..._.difference(tableStore.viewingTableIds, tableStore.pinnedTableIds),
  ];

  const calculateScale = (): number =>
    isNeedToScale(screenWidth) ? screenWidth / 2200 : 1;

  return (
    <GameContext.Provider value={contextValue}>
      <div className="tables-stats">
        <div className="tables-count">
          <img className="stat-icon" src="/assets/poker-table.svg" alt="lp" />
          <span>{tablesStats.tablesCount}</span>
        </div>
        <div className="live-players-count">
          <img
            className="stat-icon"
            src="/assets/player-stats.svg"
            alt="lp"
            width="26"
            height="26"
          />
          <span>{tablesStats.livePlayersCount}</span>
        </div>
        <div className="bots-count">
          <img
            className="stat-icon"
            src="/assets/bot-stats.svg"
            alt="lp"
            width="26"
            height="26"
          />
          <span>{tablesStats.botsCount}</span>
        </div>
      </div>
      <SwitchBlindsChipsButton />
      <div
        className={`multitables-container ${
          commonStore.getIsSidebarPinned() && 'pinned-sidebar'
        }`}
      >
        <div className="container-fluid">
          <div
            className={`row table-wrapper-row ${
              !isSingleTable ? 'tables-wrapper' : 'table-wrapper'
            } position-relative flex-wrap`}
            style={{
              justifyContent:
                isSingleTable || screenWidth < 1200
                  ? 'center'
                  : 'space-between',
            }}
          >
            {isSingleTable ? (
              <div
                style={{
                  transform: `${
                    singleTableGameControlButtons.isVisible &&
                    singleTableGameControlButtons.isVertical
                      ? 'translateX(-100px) '
                      : ''
                  }scale(${calculateScale()})${
                    isNeedToScale(screenWidth) ? ' translateY(-40px)' : ''
                  }`,
                }}
                className={`col-12 col-xl-11 table position-relative`}
              >
                <TableHeader
                  table={findViewingTable(tableStore.viewingTableIds[0])}
                  buttons={allButtonTypes}
                />
                <Game
                  tableId={tableStore.viewingTableIds[0]}
                  onGameControlButtonsChange={setSingleTableGameControlButtons}
                />
                <GameLogs
                  isOpen={eventLogsStore.isLogsVisible(
                    tableStore.viewingTableIds[0],
                  )}
                  tableId={tableStore.viewingTableIds[0]}
                />
              </div>
            ) : (
              sortedViewingTableIds.map(tableId => (
                <div
                  key={tableId}
                  className={`tables col-lg-10 col-xl-5 gy-5 gx-5 m-0 position-relative ${
                    commonStore.getIsSidebarPinned() && 'pinned-sidebar'
                  }`}
                >
                  <TableHeader
                    table={findViewingTable(tableId)}
                    buttons={allButtonTypes}
                  />
                  <Game tableId={tableId} />
                  <GameLogs
                    isOpen={eventLogsStore.isLogsVisible(tableId)}
                    tableId={tableId}
                  />
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </GameContext.Provider>
  );
};

export default observer(MultiTables);
